import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useRefreshState } from '../../lib/refreshState';
import { FiHome, FiChevronRight, FiArrowLeft } from 'react-icons/fi';
import FamilyBusinessesPage, { ManageAccountAccessTab } from '../bookkeeping/components/FamilyBusinessesPage';
import ManageFoldersTab from '../bookkeeping/components/ManageFoldersTab';
import UploadDocsPage from '../bookkeeping/components/UploadDocsPage';
import ProcessReceiptsTab from '../bookkeeping/components/ProcessReceiptsTab';
import DmsReportsTab from './DmsReportsTab';
import BackupDownloadPage from './BackupDownloadPage';
import DeletedItemsTab from './DeletedItemsTab';
import { confirmDiscardChanges, setFormDirty } from '../../lib/unsavedChanges';
import '../bookkeeping/components/BookkeepingPage.css';
import './DmsPage.css';
import { useMenuCollapse, MenuCollapseControls } from '../bookkeeping/components/MenuCollapse';
import { HeaderLeft, useHeaderSlot, HEADER_END_ID } from '../../components/layout/HeaderSlot';
import { createPortal } from 'react-dom';

// =============================================
// Documents (DMS) pillar — pure document management.
// Same menu shell as Bookkeeping (Masters / Transactions / Reports),
// but DMS-only sub-pages. No bookkeeping/GL here.
// =============================================

interface SubMenuItem { label: string; path: string; }
interface TopMenuItem { label: string; key: string; items: SubMenuItem[]; }

const MENU: TopMenuItem[] = [
  {
    label: 'Masters',
    key: 'masters',
    items: [
      { label: 'Family & Businesses', path: 'family-businesses' },
      { label: 'Folders',             path: 'manage-folders' },
    ],
  },
  {
    label: 'Transactions',
    key: 'transactions',
    items: [
      { label: 'Upload Documents',        path: 'upload-docs' },
      { label: 'Process Docs / Receipts', path: 'process-receipts' },
      { label: 'Browse Documents',        path: 'browse-docs' },
    ],
  },
  {
    label: 'Reports',
    key: 'reports',
    items: [
      { label: 'Overview',           path: 'reports-overview' },
      { label: 'Document List',      path: 'document-list' },
      { label: 'Deleted Items',      path: 'deleted-items' },
      { label: 'Retention Report',   path: 'retention-report' },
      { label: 'Download Documents', path: 'download-documents' },
    ],
  },
  {
    label: 'Utilities',
    key: 'utilities',
    items: [
      { label: 'Account Permissions', path: 'account-permissions' },
    ],
  },
];

function resolvePageFromHash(): { menu: string; item: SubMenuItem } | null {
  const hash = window.location.hash.replace('#', '');
  const slug = hash || '';
  if (!slug) return null;
  for (const menu of MENU) {
    const item = menu.items.find(i => i.path === slug);
    if (item) return { menu: menu.label, item };
  }
  return null;
}

const SubPagePlaceholder: React.FC<{ title: string; parentMenu: string }> = ({ title, parentMenu }) => (
  <div className="bk-subpage-placeholder">
    <div className="bk-subpage-icon">🚧</div>
    <h2>{title}</h2>
    <p>{parentMenu} → {title}</p>
    <span className="bk-subpage-badge">Coming Soon</span>
  </div>
);

const DmsLanding: React.FC<{ onMenuClick: (menuKey: string, fromLanding?: boolean) => void }> = ({ onMenuClick }) => (
  <div className="bk-landing">
    <div className="bk-landing-grid">
      <div className="bk-landing-card" onClick={() => onMenuClick('masters', true)}>
        <div className="bk-landing-card-icon masters">📋</div>
        <h3>Masters</h3>
        <p>Family &amp; businesses, document folders</p>
      </div>
      <div className="bk-landing-card" onClick={() => onMenuClick('transactions', true)}>
        <div className="bk-landing-card-icon transactions">📄</div>
        <h3>Transactions</h3>
        <p>Upload and browse documents</p>
      </div>
      <div className="bk-landing-card" onClick={() => onMenuClick('reports', true)}>
        <div className="bk-landing-card-icon reports">📊</div>
        <h3>Reports</h3>
        <p>Document lists, retention &amp; downloads</p>
      </div>
    </div>
  </div>
);

// DMS → Masters → Folders. Three tabs:
//  • Folder List   — the Process Receipts Record Room folder pane (browse/search/move),
//                    reused via ProcessReceiptsTab's foldersOnly mode (no duplication).
//  • Manage Folders — the full create/edit/owner-assignment UI (ManageFoldersTab).
//  • Reports        — a clickable dashboard that drills owner → folders → documents and
//                     hands off to the Folder List (a folder) or Process Receipts (a doc).
type FolderTabKey = 'folder-list' | 'manage' | 'reports';
const FOLDER_TABS: { key: FolderTabKey; label: string }[] = [
  { key: 'folder-list', label: 'Folder List' },
  { key: 'manage',      label: 'Manage Folders' },
  { key: 'reports',     label: 'Reports' },
];

const DmsFoldersTab: React.FC = () => {
  // Coming back from a document drill-down lands us on the Reports tab.
  // A browser refresh comes back to the same tab (lib/refreshState).
  const [tab, setTab] = useRefreshState<FolderTabKey>('folders.tab', () => {
    try {
      const t = sessionStorage.getItem('akt_dms_folders_tab');
      if (t) { sessionStorage.removeItem('akt_dms_folders_tab'); if (t === 'reports' || t === 'folder-list' || t === 'manage') return t as FolderTabKey; }
    } catch { /* ignore */ }
    return 'folder-list';
  }, v => v === 'reports' || v === 'folder-list' || v === 'manage');
  // Re-clicking the active tab remounts it (reset to landing); guard unsaved edits on switch.
  const [resetKey, setResetKey] = useState(0);
  // Folder drilled in from Reports → highlight it in the Folder List + show a back arrow.
  // A folder chosen from the top-menu Reports → Overview dashboard arrives via the
  // akt_dms_open_folder bridge (cross-section); honor it on mount.
  const [folderTarget, setFolderTarget] = useState<number | null>(() => {
    try {
      const v = sessionStorage.getItem('akt_dms_open_folder');
      if (v) { sessionStorage.removeItem('akt_dms_open_folder'); const n = Number(v); return isNaN(n) ? null : n; }
    } catch { /* ignore */ }
    return null;
  });
  const [cameFromReports, setCameFromReports] = useState(false);

  const switchTab = useCallback(async (next: FolderTabKey) => {
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    setCameFromReports(false);
    setFolderTarget(null);
    setTab(prev => {
      if (prev === next) setResetKey(k => k + 1);   // re-click → remount
      return next;
    });
  }, []);

  // Reports → click a folder name: open it in the Folder List with a Back affordance.
  const openFolder = useCallback((folderId: number) => {
    setFolderTarget(folderId);
    setCameFromReports(true);
    setTab('folder-list');
    setResetKey(k => k + 1);   // remount so the new target applies
  }, []);

  // Reports → click a document: hand off to Process Docs / Receipts (a different DMS
  // sub-page) with the doc pre-selected and a flag to return here on Back.
  const openDocument = useCallback((docId: number) => {
    try {
      sessionStorage.setItem('akt_navigate_to_doc', String(docId));
      sessionStorage.setItem('akt_dms_doc_return', 'reports');
    } catch { /* ignore */ }
    window.location.hash = 'process-receipts';
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }, []);

  const backToReports = useCallback(() => {
    setCameFromReports(false);
    setFolderTarget(null);
    setTab('reports');
    setResetKey(k => k + 1);
  }, []);

  return (
    <div className="dms-folders">
      <div className="dms-folders-tabs">
        {FOLDER_TABS.map(t => (
          <button
            key={t.key}
            className={`dms-folders-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => switchTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="dms-folders-body">
        <React.Fragment key={`${tab}-${resetKey}`}>
          {tab === 'folder-list' ? (
            <>
              {cameFromReports && (
                <button className="dms-folders-back" onClick={backToReports}>
                  <FiArrowLeft size={14} /> Back to Reports
                </button>
              )}
              <ProcessReceiptsTab
                viewMode="owner"
                selectedClientId={null}
                showBookkeeping={false}
                foldersOnly
                initialSelectedFolderId={folderTarget ?? undefined}
              />
            </>
          ) : tab === 'manage' ? (
            <ManageFoldersTab />
          ) : (
            <DmsReportsTab onOpenFolder={openFolder} onOpenDocument={openDocument} />
          )}
        </React.Fragment>
      </div>
    </div>
  );
};

const DmsPage: React.FC = () => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [activePage, setActivePage] = useState<{ menu: string; item: SubMenuItem } | null>(resolvePageFromHash());
  // Bumped when a sub-tab is re-clicked → remounts the sub-page to reset it to its landing.
  const [subResetKey, setSubResetKey] = useState(0);
  // True when we arrived at Process Receipts by drilling a document from the Reports
  // dashboard → show a "Back to Reports" arrow above the sub-page.
  const [docReturn, setDocReturn] = useState(false);
  const menuBarRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuBarRef.current && !menuBarRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenMenu(null); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Sync hash with active page
  useEffect(() => {
    const newHash = activePage ? '#' + activePage.item.path : '';
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
    }
  }, [activePage]);

  // Browser back/forward
  useEffect(() => {
    const onHashChange = () => setActivePage(resolvePageFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Show the "Back to Reports" arrow only while on Process Receipts after a Reports
  // document drill-down; clear the flag when navigating elsewhere.
  useEffect(() => {
    if (activePage?.item.path === 'process-receipts') {
      let v = false;
      try { v = sessionStorage.getItem('akt_dms_doc_return') === 'reports'; } catch { /* ignore */ }
      setDocReturn(v);
    } else {
      setDocReturn(false);
      try { sessionStorage.removeItem('akt_dms_doc_return'); } catch { /* ignore */ }
    }
  }, [activePage]);

  const backToReportsFromDoc = useCallback(() => {
    try {
      sessionStorage.removeItem('akt_dms_doc_return');
      sessionStorage.setItem('akt_dms_folders_tab', 'reports');
    } catch { /* ignore */ }
    setDocReturn(false);
    window.location.hash = 'manage-folders';
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }, []);

  const handleMenuClick = useCallback(async (menuKey: string, fromLanding = false) => {
    if (!fromLanding && !(await confirmDiscardChanges())) return;
    const menu = MENU.find(m => m.key === menuKey);
    setOpenMenu(null);
    if (menu && menu.items.length > 0) {
      setFormDirty(false);
      setActivePage({ menu: menu.label, item: menu.items[0] });
    }
  }, []);

  const handleSubItemClick = useCallback(async (menu: TopMenuItem, item: SubMenuItem) => {
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    // Re-clicking the active sub-tab resets it to its landing (remounts via the key below).
    if (activePage && activePage.item.path === item.path) {
      setSubResetKey(k => k + 1);
    }
    setActivePage({ menu: menu.label, item });
    setOpenMenu(null);
  }, [activePage]);

  // Reports → Overview dashboard hand-offs (top-menu version):
  //   a document → Process Docs / Receipts (pre-selected via the sessionStorage bridge);
  //   a folder   → Masters → Folders → Folder List (pre-selected via akt_dms_open_folder).
  const openDocTop = useCallback((docId: number) => {
    try { sessionStorage.setItem('akt_navigate_to_doc', String(docId)); } catch { /* ignore */ }
    window.location.hash = 'process-receipts';
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }, []);
  const openFolderTop = useCallback((folderId: number) => {
    try {
      sessionStorage.setItem('akt_dms_open_folder', String(folderId));
      sessionStorage.setItem('akt_dms_folders_tab', 'folder-list');
    } catch { /* ignore */ }
    window.location.hash = 'manage-folders';
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }, []);

  // Fold the menu rows away for more room (arrow + pin, see MenuCollapse.tsx).
  const menuCollapse = useMenuCollapse(activePage ? activePage.item.path : null);
  const headerEnd = useHeaderSlot(HEADER_END_ID);   // desktop: far right of the header row
  const activeMenuObj = activePage ? MENU.find(m => m.label === activePage.menu) ?? null : null;

  const renderSubPage = () => {
    if (!activePage) return null;
    switch (activePage.item.path) {
      case 'family-businesses':
        return <FamilyBusinessesPage scope="self" />;
      case 'manage-folders':
        return <DmsFoldersTab />;
      case 'upload-docs':
        return <UploadDocsPage />;
      case 'process-receipts':
        // DMS = document filing only (no bookkeeping panel).
        return <ProcessReceiptsTab viewMode="owner" selectedClientId={null} showBookkeeping={false} />;
      case 'account-permissions':
        return <ManageAccountAccessTab />;
      case 'download-documents':
        return <BackupDownloadPage />;
      case 'deleted-items':
        return <DeletedItemsTab />;
      case 'reports-overview':
        return <DmsReportsTab onOpenFolder={openFolderTop} onOpenDocument={openDocTop} />;
      default:
        return <SubPagePlaceholder title={activePage.item.label} parentMenu={activePage.menu} />;
    }
  };

  const menuFolded = !!activePage && menuCollapse.collapsed;

  return (
    <div className="bk-page">
      {/* Breadcrumbs */}
      {activePage && (
        <HeaderLeft>
<nav className="bk-breadcrumbs" aria-label="Breadcrumb">
          <span className="bk-breadcrumb-item bk-breadcrumb-link" onClick={async () => { if (!(await confirmDiscardChanges())) return; setFormDirty(false); window.location.href = '/dashboard'; }}>
            <FiHome size={13} /> Home
          </span>
          <FiChevronRight size={12} className="bk-breadcrumb-sep" />
          <span className="bk-breadcrumb-item bk-breadcrumb-link" onClick={async () => { if (!(await confirmDiscardChanges())) return; setFormDirty(false); setActivePage(null); }}>
            Documents (DMS)
          </span>
          <FiChevronRight size={12} className="bk-breadcrumb-sep" />
          <span className="bk-breadcrumb-item bk-breadcrumb-link" onClick={() => activeMenuObj && handleMenuClick(activeMenuObj.key)}>
            {activePage.menu}
          </span>
          <FiChevronRight size={12} className="bk-breadcrumb-sep" />
          <span className="bk-breadcrumb-item bk-breadcrumb-current">{activePage.item.label}</span>
          {menuFolded && !headerEnd && <MenuCollapseControls menu={menuCollapse} className="bk-menu-collapse-crumbs" />}
        </nav>
</HeaderLeft>
      )}

      {menuFolded && headerEnd && createPortal(<MenuCollapseControls menu={menuCollapse} className="bk-menu-collapse-crumbs" />, headerEnd)}

      {/* Top Menu Bar */}
      {!menuFolded && (
      <div className="bk-menu-bar" ref={menuBarRef}>
        {MENU.map(menu => (
          <div
            key={menu.key}
            className="bk-menu-item-wrapper"
            onMouseEnter={() => setOpenMenu(menu.key)}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <button
              className={`bk-menu-btn ${openMenu === menu.key ? 'active' : ''} ${activePage?.menu === menu.label ? 'current' : ''}`}
              onClick={() => handleMenuClick(menu.key)}
            >
              {menu.label}
            </button>

            {openMenu === menu.key && menu.items.length > 0 && (
              <div className="bk-dropdown">
                {menu.items.map(item => (
                  <button
                    key={item.path}
                    className={`bk-dropdown-item ${activePage?.item.path === item.path ? 'active' : ''}`}
                    onClick={() => handleSubItemClick(menu, item)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {/* Secondary Sub-Nav */}
      {!menuFolded && activePage && activeMenuObj && activeMenuObj.items.length >= 1 && (
        <div className="bk-sub-nav-bar">
          <div className="bk-sub-nav">
            {activeMenuObj.items.map(item => (
              <button
                key={item.path}
                className={`bk-sub-nav-item ${activePage.item.path === item.path ? 'active' : ''}`}
                onClick={() => handleSubItemClick(activeMenuObj, item)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <MenuCollapseControls menu={menuCollapse} />
        </div>
      )}

      {/* Content */}
      <div className="bk-content">
        {docReturn && activePage?.item.path === 'process-receipts' && (
          <button className="dms-folders-back" onClick={backToReportsFromDoc}>
            <FiArrowLeft size={14} /> Back to Reports
          </button>
        )}
        {activePage ? (
          // Keyed by path + reset counter so re-clicking the active sub-tab remounts it.
          <React.Fragment key={`${activePage.item.path}-${subResetKey}`}>
            {renderSubPage()}
          </React.Fragment>
        ) : (
          <DmsLanding onMenuClick={handleMenuClick} />
        )}
      </div>
    </div>
  );
};

export default DmsPage;
